import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, TrendingUp, Users, Zap, Ban, IndianRupee, Wallet, Mail } from "lucide-react";
import { adminAxios, formatINR } from "./adminApi";

// Inline SVG chart — daily scan volume
function ScanVolumeChart({ data }) {
  if (!data?.length) return null;
  const w = 720;
  const h = 200;
  const pad = { l: 36, r: 12, t: 14, b: 22 };
  const iw = w - pad.l - pad.r;
  const ih = h - pad.t - pad.b;
  const max = Math.max(...data.map((d) => d.v), 1);
  const step = iw / (data.length - 1 || 1);
  const coords = data.map((d, i) => [pad.l + i * step, pad.t + ih - (d.v / max) * ih]);
  const line = coords.map((c, i) => (i === 0 ? `M ${c[0]} ${c[1]}` : `L ${c[0]} ${c[1]}`)).join(" ");
  const area = `${line} L ${pad.l + iw} ${pad.t + ih} L ${pad.l} ${pad.t + ih} Z`;
  const gridYs = [0.25, 0.5, 0.75, 1].map((r) => pad.t + ih * (1 - r));
  const yTicks = [0.25, 0.5, 0.75, 1].map((r) => Math.round(max * r));
  const first = data[0]?.date?.slice(5) || "";
  const mid = data[Math.floor(data.length / 2)]?.date?.slice(5) || "";
  const last = data[data.length - 1]?.date?.slice(5) || "";
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id="scan-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#1F4A38" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#1F4A38" stopOpacity="0" />
        </linearGradient>
      </defs>
      {gridYs.map((y, i) => (
        <g key={i}>
          <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="#E7E4D8" strokeWidth="1" strokeDasharray="3 4" />
          <text x={pad.l - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#8A857A" fontFamily="ui-monospace, monospace">
            {yTicks[i]}
          </text>
        </g>
      ))}
      <path d={area} fill="url(#scan-fill)" />
      <path d={line} fill="none" stroke="#1F4A38" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <text x={pad.l} y={h - 4} fontSize="9" fill="#8A857A" fontFamily="ui-monospace, monospace">{first}</text>
      <text x={pad.l + iw / 2} y={h - 4} textAnchor="middle" fontSize="9" fill="#8A857A" fontFamily="ui-monospace, monospace">{mid}</text>
      <text x={pad.l + iw} y={h - 4} textAnchor="end" fontSize="9" fill="#8A857A" fontFamily="ui-monospace, monospace">{last}</text>
    </svg>
  );
}

function StatCard({ label, value, sub, icon: Ico, to, testId }) {
  return (
    <div
      data-testid={testId}
      className="group rounded-lg border border-lm-border bg-lm-surface p-4 flex flex-col hover:border-lm-primary/40 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">{label}</div>
          <div className="text-3xl font-display font-semibold text-lm-text mt-1 tracking-tight">{value ?? "—"}</div>
          {sub && <div className="text-[11px] text-lm-text3 font-mono mt-0.5">{sub}</div>}
        </div>
        {Ico && (
          <div className="w-8 h-8 rounded-md bg-lm-tint2 text-lm-primary flex items-center justify-center shrink-0">
            <Ico className="w-4 h-4" />
          </div>
        )}
      </div>
      {to && (
        <Link
          to={to}
          className="mt-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-lm-primary hover:text-lm-primaryHover"
          data-testid={`${testId}-drill`}
        >
          View users <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminAxios().get("/stats");
        setD(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load stats");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="text-lm-text2" data-testid="admin-dashboard-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading stats…</div>;
  }
  if (!d) return null;

  const topCat = d.top_categories?.[0];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-lm-secondary">Global overview</div>
        <h1 className="mt-1 text-2xl md:text-3xl font-display font-semibold tracking-tight text-lm-text">
          Dashboard
        </h1>
        <p className="text-sm text-lm-text2 mt-1">Fleet-wide metrics across every workspace on Leadmato.</p>
      </div>

      {/* Row 1 — key counts with drill-through */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
        <StatCard label="Total users" value={d.total_users} icon={Users} to="/admin/users" testId="stat-total-users" />
        <StatCard label="Active today" value={d.active_today} icon={Zap} to="/admin/users?sort=last_active" testId="stat-active-today" sub="last 24h" />
        <StatCard label="Free plan" value={d.free_plan_count} to="/admin/users?plan=free" testId="stat-free" />
        <StatCard label="Paid users" value={d.paid_plan_count} to="/admin/users?plan=paid" testId="stat-paid" />
        <StatCard label="Suspended" value={d.suspended_count} icon={Ban} to="/admin/users?status=suspended" testId="stat-suspended" />
        {topCat ? (
          <StatCard
            label="Top category"
            value={topCat.count}
            sub={topCat.label || topCat.category}
            to={`/admin/users?category=${encodeURIComponent(topCat.category)}`}
            testId="stat-top-category"
          />
        ) : (
          <StatCard label="Top category" value="—" testId="stat-top-category" />
        )}
      </div>

      {/* Revenue + scans */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div data-testid="revenue-card" className="lg:col-span-1 rounded-lg border border-lm-border bg-lm-surface p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Revenue</div>
              <div className="text-3xl font-display font-semibold text-lm-primary mt-1 tracking-tight">
                {formatINR(d.revenue_paise)}
              </div>
              <div className="text-[11px] font-mono text-lm-text3 mt-0.5">{d.revenue_txn_count} paid txns · all time</div>
            </div>
            <div className="w-9 h-9 rounded-md bg-lm-tint2 text-lm-primary flex items-center justify-center">
              <IndianRupee className="w-4 h-4" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="rounded border border-lm-border p-2">
              <div className="text-[9px] font-mono uppercase text-lm-text3">This month</div>
              <div className="font-semibold text-lm-text mt-0.5">{formatINR(d.revenue_this_month_paise)}</div>
            </div>
            <div className="rounded border border-lm-border p-2">
              <div className="text-[9px] font-mono uppercase text-lm-text3">Last month</div>
              <div className="font-semibold text-lm-text mt-0.5">{formatINR(d.revenue_last_month_paise)}</div>
            </div>
            <div className="rounded border border-lm-border p-2 bg-lm-tint2/40">
              <div className="text-[9px] font-mono uppercase text-lm-text3">MRR est</div>
              <div className="font-semibold text-lm-primary mt-0.5">{formatINR(d.mrr_estimate_paise)}</div>
            </div>
          </div>
        </div>

        <div data-testid="scan-volume-card" className="lg:col-span-2 rounded-lg border border-lm-border bg-lm-surface p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Scan volume</div>
              <div className="text-sm font-medium text-lm-text">Last 30 days</div>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-widest text-lm-primary bg-lm-tint2 border border-lm-tint2 px-2 py-0.5 rounded inline-flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> {d.scans_today} today
            </span>
          </div>
          <div className="h-40 -mx-1">
            <ScanVolumeChart data={d.scan_volume_30d || []} />
          </div>
        </div>
      </div>

      {/* Onboarding funnel */}
      {d.onboarding_funnel && <OnboardingFunnel funnel={d.onboarding_funnel} />}

      {/* Categories + plan mix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-lm-border bg-lm-surface p-5" data-testid="top-categories-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3">Top categories</div>
            <span className="text-[10px] font-mono text-lm-text3">by workspaces</span>
          </div>
          {(!d.top_categories || d.top_categories.length === 0) ? (
            <div className="text-sm text-lm-text3">No categories yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {d.top_categories.map((r) => {
                const pct = Math.round((r.count / Math.max(d.top_categories[0].count, 1)) * 100);
                return (
                  <li key={r.category} className="group">
                    <Link
                      to={`/admin/users?category=${encodeURIComponent(r.category)}`}
                      className="flex items-center gap-3 py-1.5 hover:bg-lm-tint/50 rounded px-2 -mx-2"
                    >
                      <span className="text-xs text-lm-text truncate flex-1">{r.label || r.category}</span>
                      <div className="w-24 h-1.5 rounded-full bg-lm-tint overflow-hidden">
                        <div className="h-full bg-lm-primary" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-mono font-semibold text-lm-primary w-8 text-right">{r.count}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="rounded-lg border border-lm-border bg-lm-surface p-5" data-testid="plan-mix-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3">Plan distribution</div>
            <Link to="/admin/ledger" className="text-[10px] font-mono uppercase tracking-widest text-lm-primary hover:text-lm-primaryHover inline-flex items-center gap-1">
              <Wallet className="w-3 h-3" /> Ledger
            </Link>
          </div>
          <ul className="space-y-1.5">
            {(d.plan_mix || []).map((r) => {
              const max = Math.max(...d.plan_mix.map((p) => p.count), 1);
              const pct = Math.round((r.count / max) * 100);
              return (
                <li key={r.plan}>
                  <Link
                    to={`/admin/users?plan=${encodeURIComponent(r.plan)}`}
                    className="flex items-center gap-3 py-1.5 hover:bg-lm-tint/50 rounded px-2 -mx-2"
                  >
                    <span className="text-xs font-mono text-lm-text uppercase tracking-widest flex-1 truncate">{r.plan}</span>
                    <div className="w-24 h-1.5 rounded-full bg-lm-tint overflow-hidden">
                      <div className="h-full bg-lm-primary" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-mono font-semibold text-lm-primary w-8 text-right">{r.count}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ================= Onboarding funnel =================
// A horizontal-bar activation funnel showing what fraction of workspaces
// completed each onboarding step. Each bar drills through to the users list
// filtered to workspaces at (or beyond) that step where possible.
function OnboardingFunnel({ funnel }) {
  const [busy, setBusy] = useState(false);
  const total = Math.max(funnel.total || 0, 1);
  const steps = funnel.steps || [];
  const first = steps[0]?.count ?? 0;
  const last = steps[steps.length - 1]?.count ?? 0;
  const activationPct = Math.round((last / total) * 100);
  // Users stuck before step 3 (brand_created) — total minus brand_created count
  const brandCreated = steps.find((s) => s.key === "brand_created")?.count ?? 0;
  const stuckBeforeBrand = Math.max(0, (funnel.total || 0) - brandCreated);

  const bulkNudge = async () => {
    if (busy || stuckBeforeBrand === 0) return;
    if (!window.confirm(`Send onboarding reminder to ${stuckBeforeBrand} user${stuckBeforeBrand === 1 ? "" : "s"} stuck before brand import? (48h cooldown per user is respected.)`)) return;
    setBusy(true);
    try {
      const r = await adminAxios().post("/nudge/bulk-brand-import");
      const d = r.data;
      toast.success(`Sent ${d.sent} · skipped ${d.skipped_cooldown} on cooldown · ${d.skipped_no_email} without email`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk nudge failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="onboarding-funnel-card"
      className="rounded-lg border border-lm-border bg-lm-surface p-5 mb-4"
    >
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Onboarding funnel</div>
          <div className="text-sm font-medium text-lm-text">Activation across {funnel.total} workspace{funnel.total === 1 ? "" : "s"}</div>
        </div>
        <div className="flex items-baseline gap-4">
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Full activation</div>
            <div className="text-2xl font-display font-semibold text-lm-primary tracking-tight">{activationPct}%</div>
          </div>
          <div className="text-right border-l border-lm-border pl-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-lm-text3">Drop-off</div>
            <div className="text-lg font-display font-semibold text-lm-text tracking-tight">{first - last}</div>
            <div className="text-[10px] font-mono text-lm-text3">signed up → confirmed</div>
          </div>
        </div>
      </div>

      {stuckBeforeBrand > 0 && (
        <div
          data-testid="funnel-bulk-nudge-row"
          className="mb-4 flex items-center justify-between gap-3 flex-wrap rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2"
        >
          <div className="text-xs text-amber-900">
            <span className="font-semibold">{stuckBeforeBrand}</span> user{stuckBeforeBrand === 1 ? "" : "s"} haven't imported their website yet — the biggest quality lever for scans.
          </div>
          <button
            onClick={bulkNudge}
            disabled={busy}
            data-testid="funnel-bulk-nudge"
            className="text-xs px-3 py-1.5 rounded-md bg-lm-primary text-white font-semibold hover:bg-lm-primaryHover disabled:opacity-60 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            Nudge stuck users
          </button>
        </div>
      )}

      <div className="space-y-2.5">
        {steps.map((s, i) => {
          const pct = Math.round((s.count / total) * 100);
          const prev = i > 0 ? steps[i - 1].count : total;
          const stepDrop = prev - s.count;
          const dropPct = prev > 0 ? Math.round((stepDrop / prev) * 100) : 0;
          return (
            <div key={s.key} data-testid={`funnel-step-${s.key}`}>
              <div className="flex items-center gap-3 text-sm">
                <span className="w-6 h-6 rounded-md bg-lm-tint2 text-lm-primary font-mono font-semibold text-[11px] flex items-center justify-center shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="flex-1 min-w-0 text-lm-text truncate">{s.label}</span>
                <span className="font-mono text-xs text-lm-text3 whitespace-nowrap">
                  {s.count} of {funnel.total} users
                </span>
                <span className="font-mono font-semibold text-sm text-lm-primary w-12 text-right">{pct}%</span>
              </div>
              <div className="mt-1.5 ml-9 h-2 rounded-full bg-lm-tint overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-lm-primary to-lm-primary/70 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              {i > 0 && stepDrop > 0 && (
                <div className="ml-9 mt-1 text-[10px] font-mono uppercase tracking-widest text-red-600">
                  ↓ {stepDrop} dropped ({dropPct}% of previous step)
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

